import { useTranslation } from "gatsby-plugin-react-i18next";

import { Text } from "@theme/components";

import type { ThemeUIStyleObject } from "@theme/components";
import type { SiteData, StrapiProductBadgesInterface } from "@theme/types";
import type { FC } from "react";

/**
 * Get Product Badge
 *
 * This function takes in the product badges object and the current instance and returns the badge variant
 *
 * includes a fallback in case no badge is found in the hierarchy object
 *
 *  */
export const getProductBadgeVariant = (
  productBadges: StrapiProductBadgesInterface | null,
  currentMsMarket: string,
): ProductBadgeProps | null => {
  if (productBadges) {
    const badgeHierarchy: ProductBadgeProps["variant"][] = [
      "clearance",
      "sale",
      "almostGone",
      "new",
      "moreColors",
      "bestSeller",
      "specialEdition",
      "limitedEdition",
      "freeGift",
      "trending",
      "exclusive",
      "premium",
    ];

    let badgeVariant = badgeHierarchy.find((hierarchyBadge) => {
      const sites: SiteData[] = productBadges[hierarchyBadge as keyof StrapiProductBadgesInterface];

      return sites?.some((site) => site.instance.toLowerCase() === currentMsMarket);
    });

    // Fallback: If no badge is found in the hierarchy, find the first available badge
    if (!badgeVariant) {
      const allBadges = Object.keys(productBadges) as (keyof typeof productBadges)[];
      badgeVariant = allBadges.find((badgeKey) => {
        const sites: SiteData[] = productBadges[badgeKey as keyof StrapiProductBadgesInterface];

        return sites?.some((site) => site.instance.toLowerCase() === currentMsMarket);
      });
    }

    return badgeVariant ? { variant: badgeVariant } : null;
  }

  return null;
};

export interface ProductBadgeProps {
  // If adding a new variant, make sure to update the getProductBadgeVariant hierarchy in the above function
  variant:
    | "clearance"
    | "sale"
    | "almostGone"
    | "new"
    | "moreColors"
    | "bestSeller"
    | "specialEdition"
    | "limitedEdition"
    | "freeGift"
    | "trending"
    | "exclusive"
    | "premium";
  sx?: ThemeUIStyleObject;
  className?: string;
}

const getBadgeStyles = (variant: ProductBadgeProps["variant"]) => {
  switch (variant) {
    case "clearance":
      return { bg: "white", color: "critical500" };
    case "sale":
      return { bg: "critical500", color: "white" };
    case "new":
    case "exclusive":
      return { bg: "navy", color: "white" };
    case "bestSeller":
    case "specialEdition":
      return { bg: "goldDark", color: "white" };
    case "almostGone":
    case "premium":
    case "limitedEdition":
      return { bg: "grey700", color: "white" };
    case "trending":
    case "freeGift":
      return { bg: "goldLight", color: "grey700" };
    case "moreColors":
      return { background: "goldCharcoalGradient", color: "white" };
    default:
      return {};
  }
};

export const ProductBadge: FC<ProductBadgeProps> = ({ variant, sx, className }) => {
  const { t } = useTranslation("navigation");

  const productBadgeText = {
    clearance: t("commonClearanceBadge", { defaultValue: "Clearance" }),
    sale: t("commonSaleBadge", { defaultValue: "Sale" }),
    almostGone: t("commonAlmostGoneBadge", { defaultValue: "Almost Gone" }),
    new: t("commonNew", { defaultValue: "New" }),
    moreColors: t("commonMoreColorsBadge", { defaultValue: "Multiple Colors" }),
    bestSeller: t("commonBestSeller", { defaultValue: "Best Seller" }),
    premium: t("commonPremium", { defaultValue: "Premium" }),
    trending: t("commonTrending", { defaultValue: "Trending" }),
    freeGift: t("commonFreeGiftBadge", { defaultValue: "Free Gift" }),
    exclusive: t("commonExclusiveBadge", { defaultValue: "Exclusive" }),
    limitedEdition: t("commonLimitedEditionBadge", { defaultValue: "Limited Edition" }),
    specialEdition: t("commonSpecialEditionBadge", { defaultValue: "Special Edition" }),
  };

  return (
    <Text
      className={className}
      data-testid="div-new-productBadge"
      variant="sansMicro01MediumPlus"
      sx={{
        bg: "goldPrimary",
        mb: [0, null, "s100"],
        px: "s100",
        py: "s50",
        borderRadius: "xs",
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        ...getBadgeStyles(variant),
        ...sx,
      }}
    >
      {productBadgeText[variant]}
    </Text>
  );
};
